import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Plus,
  Save,
  Trash2,
  CheckCircle2,
  XCircle,
  Wand2,
  Search,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/useToast'
import {
  codeStructuresApi,
  type CodeStructure,
  type CodeStructureSegment,
  type ProductGroupStructureItem,
} from '@/lib/api'

const PRESET: CodeStructure = {
  segments: [
    { ai: '01', required: true, length: 14 },
    { ai: '21', required: true, min_length: 7, max_length: 20 },
    { ai: '93', required: true, length: 4 },
  ],
  forbid_cyrillic: true,
  custom_regex: null,
  notes: '',
}

const clone = (s: CodeStructure | null | undefined): CodeStructure =>
  JSON.parse(JSON.stringify(s ?? PRESET))

type PreviewState = { ok: boolean; reason: string; parsed: Record<string, string> }

// ─── subcomponents ───

/** «14» → {length:14}; «7-20» → {min_length:7, max_length:20}; «7-» → {min_length:7}; «-20» → {max_length:20}; «» → {} */
function parseLengthSpec(spec: string): Pick<CodeStructureSegment, 'length' | 'min_length' | 'max_length'> {
  const s = spec.trim()
  if (!s) return {}
  if (/^\d+$/.test(s)) return { length: +s }
  const m = s.match(/^(\d*)\s*-\s*(\d*)$/)
  if (m) {
    const [, lo, hi] = m
    const out: Pick<CodeStructureSegment, 'length' | 'min_length' | 'max_length'> = {}
    if (lo) out.min_length = +lo
    if (hi) out.max_length = +hi
    return out
  }
  return {}
}

function formatLengthSpec(seg: CodeStructureSegment): string {
  if (seg.length != null) return String(seg.length)
  const lo = seg.min_length
  const hi = seg.max_length
  if (lo != null && hi != null) return `${lo}-${hi}`
  if (lo != null) return `${lo}-`
  if (hi != null) return `-${hi}`
  return ''
}

function SegmentRow({
  seg,
  onChange,
  onDelete,
}: {
  seg: CodeStructureSegment
  onChange: (patch: Partial<CodeStructureSegment>) => void
  onDelete: () => void
}) {
  const [lenInput, setLenInput] = useState(formatLengthSpec(seg))
  // sync external changes (preset apply)
  useEffect(() => {
    setLenInput(formatLengthSpec(seg))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg.length, seg.min_length, seg.max_length])

  const commitLen = (v: string) => {
    setLenInput(v)
    onChange({ length: undefined, min_length: undefined, max_length: undefined, ...parseLengthSpec(v) })
  }

  return (
    <div className="grid grid-cols-12 gap-2 items-center bg-white rounded p-2 border">
      <Input
        className="col-span-3"
        value={seg.ai}
        placeholder="01"
        onChange={(e) => onChange({ ai: e.target.value.replace(/\D/g, '').slice(0, 4) })}
      />
      <Input
        className="col-span-8"
        value={lenInput}
        placeholder="Напр. 14 или 7-20"
        onChange={(e) => commitLen(e.target.value)}
      />
      <div className="col-span-1 text-right">
        <button type="button" onClick={onDelete} className="text-red-600 hover:text-red-800" title="Удалить">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

function PreviewBox({ preview }: { preview: PreviewState }) {
  const cls = preview.ok ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
  const entries = Object.entries(preview.parsed ?? {}).filter(([k]) => !k.startsWith('__'))
  return (
    <div className={`rounded p-3 text-sm border ${cls}`}>
      <div className="font-medium flex items-center gap-1">
        {preview.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        {preview.ok ? 'Код валиден' : `Ошибка: ${preview.reason}`}
      </div>
      {entries.length > 0 && (
        <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs font-mono">
          {entries.map(([ai, data]) => (
            <div key={ai}>
              <span className="text-gray-500">AI({ai})</span>: {data}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── accordion row ───

function GroupAccordionRow({
  item,
  open,
  onToggle,
  onSaved,
}: {
  item: ProductGroupStructureItem
  open: boolean
  onToggle: () => void
  onSaved: () => Promise<void>
}) {
  const { toast } = useToast()
  const [draft, setDraft] = useState<CodeStructure>(clone(item.structure))
  const [saving, setSaving] = useState(false)
  const [testCode, setTestCode] = useState('')
  const [preview, setPreview] = useState<PreviewState | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  // reset draft when item changes or accordion opens
  useEffect(() => {
    if (open) {
      setDraft(clone(item.structure))
      setPreview(null)
      setTestCode('')
    }
  }, [open, item.structure])

  const patchSeg = (idx: number, patch: Partial<CodeStructureSegment>) =>
    setDraft((d) => ({ ...d, segments: d.segments.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }))

  const handleSave = async () => {
    setSaving(true)
    try {
      await codeStructuresApi.save(item.code, draft)
      toast.success('Структура кода сохранена')
      await onSaved()
    } catch (e) {
      toast.error('Ошибка сохранения: ' + (e instanceof Error ? e.message : 'unknown'))
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    if (!item.configured) return
    if (!window.confirm('Сбросить структуру для этой группы? Сканирование будет заблокировано.')) return
    setSaving(true)
    try {
      await codeStructuresApi.clear(item.code)
      toast.success('Структура сброшена')
      await onSaved()
    } catch {
      toast.error('Ошибка сброса')
    } finally {
      setSaving(false)
    }
  }

  const handleValidate = async () => {
    if (!testCode.trim()) return
    setPreviewLoading(true)
    try {
      const r = await codeStructuresApi.validate(item.code, testCode, draft)
      setPreview(r.data as PreviewState)
    } catch (e) {
      toast.error('Ошибка проверки: ' + (e instanceof Error ? e.message : 'unknown'))
    } finally {
      setPreviewLoading(false)
    }
  }

  return (
    <>
      <tr
        className={`border-b cursor-pointer hover:bg-gray-50 ${open ? 'bg-blue-50' : ''}`}
        onClick={onToggle}
      >
        <td className="px-3 py-2 w-8 text-gray-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-2 font-mono text-xs text-gray-600">{item.code}</td>
        <td className="px-3 py-2 font-medium">{item.name}</td>
        <td className="px-3 py-2 text-sm">
          {item.configured ? (
            <span className="inline-flex items-center gap-1 text-green-700">
              <CheckCircle2 className="w-4 h-4" /> настроена
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-red-600">
              <XCircle className="w-4 h-4" /> не настроена
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-xs text-gray-500">
          {item.structure?.segments?.length
            ? `${item.structure.segments.length} сегмент(ов)`
            : '—'}
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={5} className="bg-gray-50 p-4 border-b">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Настройте обязательные AI и правила валидации для этой группы
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setDraft(clone(null))}>
                    <Wand2 className="w-4 h-4 mr-1" /> Базовый пресет
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleClear} disabled={!item.configured || saving}>
                    <Trash2 className="w-4 h-4 mr-1" /> Сбросить
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Сегменты (Application Identifiers):</div>
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-600 px-2">
                  <div className="col-span-3">AI</div>
                  <div className="col-span-8">Длина («14» или «7-20»)</div>
                  <div className="col-span-1" />
                </div>
                {draft.segments.map((seg, i) => (
                  <SegmentRow
                    key={i}
                    seg={seg}
                    onChange={(p) => patchSeg(i, p)}
                    onDelete={() =>
                      setDraft((d) => ({ ...d, segments: d.segments.filter((_, j) => j !== i) }))
                    }
                  />
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setDraft((d) => ({ ...d, segments: [...d.segments, { ai: '', required: true }] }))
                  }
                >
                  <Plus className="w-4 h-4 mr-1" /> Добавить сегмент
                </Button>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.forbid_cyrillic}
                    onChange={(e) => setDraft({ ...draft, forbid_cyrillic: e.target.checked })}
                  />
                  Запрещать кириллицу в коде
                </label>
                <div>
                  <label className="text-sm font-medium block mb-1">
                    Дополнительный regex (advanced, опционально):
                  </label>
                  <Input
                    value={draft.custom_regex ?? ''}
                    placeholder="^01\d{14}21\w+"
                    onChange={(e) => setDraft({ ...draft, custom_regex: e.target.value || null })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Заметки:</label>
                  <Input
                    value={draft.notes ?? ''}
                    placeholder="Например: методичка ЦРПТ для обуви"
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t pt-3 space-y-2">
                <div className="text-sm font-medium">Проверить код:</div>
                <div className="flex gap-2">
                  <Input
                    value={testCode}
                    placeholder="(01)04601234567893(21)...(93)xxxx или raw"
                    onChange={(e) => setTestCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleValidate()
                    }}
                  />
                  <Button variant="secondary" size="sm" onClick={handleValidate} isLoading={previewLoading}>
                    Проверить
                  </Button>
                </div>
                {preview && <PreviewBox preview={preview} />}
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSave} isLoading={saving}>
                  <Save className="w-4 h-4 mr-1" /> Сохранить структуру
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ─── main page ───

export function CodeStructuresPage() {
  const { toast } = useToast()
  const [items, setItems] = useState<ProductGroupStructureItem[]>([])
  const [loading, setLoading] = useState(true)
  const [openCode, setOpenCode] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<'all' | 'configured' | 'not_configured'>('all')

  const load = async () => {
    setLoading(true)
    try {
      const r = await codeStructuresApi.list()
      const list = (r.data as { items: ProductGroupStructureItem[] }).items ?? []
      setItems(list)
    } catch {
      toast.error('Не удалось загрузить товарные группы')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((i) => {
      if (filter === 'configured' && !i.configured) return false
      if (filter === 'not_configured' && i.configured) return false
      if (!q) return true
      return i.code.toLowerCase().includes(q) || i.name.toLowerCase().includes(q)
    })
  }, [items, query, filter])

  const stats = useMemo(() => {
    const total = items.length
    const configured = items.filter((i) => i.configured).length
    return { total, configured }
  }, [items])

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/admin/settings" className="text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold">Структуры кодов маркировки</h1>
      </div>

      <p className="text-sm text-gray-500">
        Для каждой товарной группы задайте структуру GS1-кода: какие Application Identifiers
        обязательны, их длины, контрольные суммы. Сканирование задачи блокируется, если для её
        группы структура не настроена.
      </p>

      <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            className="pl-8"
            placeholder="Поиск по коду или названию…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-1 text-sm">
          {(['all', 'configured', 'not_configured'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded border ${
                filter === f ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {f === 'all' ? 'Все' : f === 'configured' ? 'Настроенные' : 'Не настроенные'}
            </button>
          ))}
        </div>
        <div className="text-sm text-gray-500 ml-auto">
          {stats.configured} из {stats.total} групп настроены
        </div>
      </div>

      {loading ? (
        <LoadingSpinner className="my-8" />
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 text-xs uppercase">
              <tr>
                <th className="w-8" />
                <th className="px-3 py-2 text-left">Код</th>
                <th className="px-3 py-2 text-left">Название</th>
                <th className="px-3 py-2 text-left">Статус</th>
                <th className="px-3 py-2 text-left">Состав</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                    {items.length === 0 ? 'Нет товарных групп' : 'Ничего не найдено'}
                  </td>
                </tr>
              ) : (
                filtered.map((item) => (
                  <GroupAccordionRow
                    key={item.code}
                    item={item}
                    open={openCode === item.code}
                    onToggle={() => setOpenCode(openCode === item.code ? null : item.code)}
                    onSaved={load}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
